// @ts-nocheck

import React, { useMemo } from 'react';
import styled, { css, keyframes } from 'styled-components';

import { KLValidIcon, KLWrongIcon } from '../assets/img';
import { KLRound3StepAssetByCollectibleId } from '../constants/assets';
import { KLRound3StepIdsByOrder } from '../constants/order';
import SharedStyle from '../styled-component/shared';
import {
  KLCollectibleIds,
  KLCollectibleStatuses,
  type KLCollectibleId,
  type KLCollectibleStatus,
} from '../types/collectible';

export const KL_ORIENTATION_STEP_IMG_ASPECT_RATIO = 1.34;

const alts = {
  [KLCollectibleIds.MARK]: 'Étape du repère',
  [KLCollectibleIds.TAG]: 'Étape de la balise',
  [KLCollectibleIds.KNIFE]: 'Étape du poignard',
};

const haloAnimation = keyframes`
  0% {
    opacity: 0;
  }
  50% {
    opacity: 1;
  }
  100% {
    opacity: 0;
  }
`;

const DEFAULT_WIDTH = 5; // em

const Wrapper = styled.div`
  position: relative;
  width: ${DEFAULT_WIDTH}em;
  height: ${KL_ORIENTATION_STEP_IMG_ASPECT_RATIO * DEFAULT_WIDTH}em;

  transition: filter 200ms ease-in-out;

  cursor: pointer;

  ${({ $status, $disabled, $position }) => {
    const stepPositionPseudoElementStyle = css`
      content: '${$position}';
      position: absolute;
      top: 2%;
      left: 10%;

      color: white;
      font-weight: bold;
    `;

    const sharedStyle = css`
      filter: ${$disabled ? 'brightness(30%)' : undefined};
    `;

    if ($status === KLCollectibleStatuses.WON || $status === KLCollectibleStatuses.LOST) {
      const iconPath = $status === KLCollectibleStatuses.WON ? KLValidIcon : KLWrongIcon;

      return css`
        ${sharedStyle}

        &:before {
          ${stepPositionPseudoElementStyle}
        }

        &:after {
          content: '';
          top: -1%;
          right: 1%;
          position: absolute;
          width: 20%;
          height: 20%;
          background-image: url(${iconPath});
          background-repeat: no-repeat;
          background-size: contain;
          background-position: center;
        }
      `;
    }

    if ($status === KLCollectibleStatuses.PENDING && !$disabled) {
      return css`
        &:after {
          ${stepPositionPseudoElementStyle}
        }
      `;
    }

    return css`
      ${sharedStyle}

      &:before {
        ${stepPositionPseudoElementStyle}
      }
    `;
  }}

  ${SharedStyle.ImageAsButtonsBehaviour}
`;

const StepIcon = styled.img`
  width: 100%;
`;

const Shadow = styled.div`
  ${({ $status, $disabled }) => {
    if ($status === KLCollectibleStatuses.PENDING && !$disabled) {
      return css`
        position: absolute;
        z-index: -1;
        top: 2%;
        left: 2%;
        width: 96%;
        height: 96%;
        background: #e4b82dee;
        box-shadow: 0em 0em 0.5em 0.3em #e4b82d;

        animation: ${haloAnimation} 3000ms ease-in-out infinite;
      `;
    }
    return css`
      position: absolute;
      z-index: -1;
      top: 2%;
      left: 2%;
      width: 96%;
      height: 96%;
      background: black;
      box-shadow: 0em 0em 0.7em 0.1em black;
    `;
  }}
`;

type KLOrientationStepProps = {
  className?: string;
  stepId: KLCollectibleId;
  status: {
    value: KLCollectibleStatus;
    forceDisabling: boolean;
  };
  paginationIndex: number;
  onClick: () => void;
};

const KLOrientationStep = ({ className = '', stepId, status, paginationIndex, onClick }: KLOrientationStepProps) => {
  const assetPath = useMemo(() => KLRound3StepAssetByCollectibleId[stepId][paginationIndex], [stepId, paginationIndex]);
  const alt = useMemo(() => alts[stepId], [stepId]);
  const statusValue = useMemo(() => status.value, [status]);
  const statusForceDisabling = useMemo(() => status.forceDisabling, [status]);
  const position = useMemo(() => KLRound3StepIdsByOrder.indexOf(stepId) + 1, [stepId]);

  return (
    <Wrapper
      className={className}
      $status={statusValue}
      $disabled={statusForceDisabling}
      $position={position}
      onClick={onClick}
    >
      <StepIcon src={assetPath} alt={alt} />
      <Shadow $status={statusValue} $disabled={statusForceDisabling} />
    </Wrapper>
  );
};

export default KLOrientationStep;
